import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API, useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, CheckCircle, XCircle, Mail, Loader2, ArrowRight } from "lucide-react";

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error, expired
  const [message, setMessage] = useState("");
  const [resending, setResending] = useState(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (token) {
      verifyEmail();
    } else if (user && !user.email_verified) {
      setStatus("pending");
      setMessage("Please check your email for a verification link.");
    } else if (user && user.email_verified) {
      setStatus("success");
      setMessage("Your email is already verified!");
    }
  }, [token, user]);

  const verifyEmail = async () => {
    try {
      const res = await axios.get(`${API}/auth/verify-email?token=${token}`);
      setStatus("success");
      setMessage(res.data.message);
      checkAuth(); // Refresh user data
    } catch (error) {
      const detail = error.response?.data?.detail || "Verification failed";
      if (detail.includes("expired")) {
        setStatus("expired");
      } else {
        setStatus("error");
      }
      setMessage(detail);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const authToken = localStorage.getItem("token");
      await axios.post(
        `${API}/auth/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` }, withCredentials: true }
      );
      toast.success("Verification email sent! Check your inbox.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-slate-50 flex items-center justify-center p-6">
      {/* Logo */}
      <Link
        to="/"
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-slate-600 hover:text-navy-600 transition-colors"
      >
        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
          <TrendingUp className="w-6 h-6 text-white" />
        </div>
        <span className="font-heading font-semibold">AndInvestTrading</span>
      </Link>

      <Card className="w-full max-w-md bg-white shadow-luxury border-slate-200">
        <CardContent className="p-8 text-center">
          {/* Verifying State */}
          {status === "verifying" && (
            <>
              <div className="w-20 h-20 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-navy-600 animate-spin" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy-700 mb-2">
                Verifying Your Email
              </h1>
              <p className="text-slate-500">Please wait while we verify your email address...</p>
            </>
          )}

          {/* Pending State (no token, waiting for verification) */}
          {status === "pending" && (
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy-700 mb-2">
                Verify Your Email
              </h1>
              <p className="text-slate-500 mb-6">{message}</p>
              <p className="text-sm text-slate-400 mb-6">
                The verification link will expire in 15 minutes.
              </p>
              <Button
                onClick={handleResendVerification}
                disabled={resending}
                className="w-full btn-gold"
                data-testid="resend-verification-btn"
              >
                {resending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Mail className="w-5 h-5 mr-2" />
                    Resend Verification Email
                  </>
                )}
              </Button>
            </>
          )}

          {/* Success State */}
          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy-700 mb-2">
                Email Verified!
              </h1>
              <p className="text-slate-500 mb-6">{message}</p>
              <Link to="/dashboard">
                <Button className="w-full btn-gold" data-testid="go-to-dashboard-btn">
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </>
          )}

          {/* Error State */}
          {status === "error" && (
            <>
              <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-rose-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy-700 mb-2">
                Verification Failed
              </h1>
              <p className="text-slate-500 mb-6">{message}</p>
              <Link to="/login">
                <Button className="w-full btn-gold">
                  Return to Login
                </Button>
              </Link>
            </>
          )}

          {/* Expired State */}
          {status === "expired" && (
            <>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-amber-600" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-navy-700 mb-2">
                Link Expired
              </h1>
              <p className="text-slate-500 mb-6">
                This verification link has expired. Please request a new one.
              </p>
              {user ? (
                <Button
                  onClick={handleResendVerification}
                  disabled={resending}
                  className="w-full btn-gold"
                >
                  {resending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Request New Verification Link"
                  )}
                </Button>
              ) : (
                <Link to="/login">
                  <Button className="w-full btn-gold">
                    Login to Request New Link
                  </Button>
                </Link>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
